import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  ACCOUNT_ROLE_TALK,
  destinationAfterVerifiedAccount,
  getAccountRole,
  showFindAListenerCta,
} from '../../util/accountRole';
import {
  identityFromUser,
  markIdentityStandinCompleteSeen,
  shouldShowIdentityStandinComplete,
} from '../../util/identity';
import {
  fetchIdentityStatus,
  markIdentityStandinCompleteSeenOnServer,
  sandboxVerifyIdentity,
  startStripeIdentityPath,
} from '../../util/api';
import { PrimaryButton } from '../Button/Button';
import NamedLink from '../NamedLink/NamedLink';
import IdentityRetryNotice from '../IdentityRetryNotice/IdentityRetryNotice';
import IdentityManualUpload from '../IdentityManualUpload/IdentityManualUpload';
import { MANUAL_PENDING_TITLE, MANUAL_PENDING_BODY } from '../../util/identityStatus';

import css from './IdentitySandboxPanel.module.css';

const ManualPendingNotice = ({ className, titleClassName, autoFocus }) => {
  const headingRef = React.useRef(null);
  useEffect(() => {
    if (autoFocus && headingRef.current) {
      headingRef.current.focus();
    }
  }, [autoFocus]);
  return (
    <div className={className} data-manual-id-pending="true" role="status" aria-live="polite">
      <h3 ref={headingRef} tabIndex={-1} className={titleClassName}>
        {MANUAL_PENDING_TITLE}
      </h3>
      <p>{MANUAL_PENDING_BODY}</p>
      <a href={startStripeIdentityPath()}>Or try the Stripe ID check again</a>
    </div>
  );
};

/**
 * Next-step actions after the TEST 18+ stand-in is complete.
 * Shown once; leaving via these links (or Dismiss) persists so it does not return.
 *
 * @param {Object} props
 * @param {Object} [props.currentUser]
 * @param {Function} [props.onLeave]
 * @returns {JSX.Element}
 */
const IdentityNextSteps = props => {
  const { currentUser, currentUserHasListings, currentUserOwnListing, onLeave } = props;
  const dest = destinationAfterVerifiedAccount(currentUser, {
    currentUserHasListings,
    currentUserOwnListing,
  });
  const dashboardTo = dest.search ? { search: dest.search } : undefined;
  const showFind = showFindAListenerCta({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
  });
  return (
    <div className={css.nextCard} role="status">
      <h3 className={css.nextTitle}>
        <FormattedMessage id="Identity.nextTitle" />
      </h3>
      <p className={css.nextLede}>
        <FormattedMessage id={showFind ? 'Identity.nextBody' : 'Identity.nextBodyListen'} />
      </p>
      <div className={css.nextActions}>
        <NamedLink
          name={dest.name || 'DashboardPage'}
          to={dashboardTo}
          className={css.primaryCta}
          onClick={onLeave}
        >
          <FormattedMessage id="Identity.goToDashboard" />
        </NamedLink>
        {showFind ? (
          <NamedLink name="SearchPage" className={css.secondaryCta} onClick={onLeave}>
            <FormattedMessage id="Identity.findAListener" />
          </NamedLink>
        ) : null}
        <button type="button" className={css.dismiss} onClick={onLeave}>
          <FormattedMessage id="Identity.dismissNext" />
        </button>
      </div>
    </div>
  );
};

const persistStandinCompleteSeen = userOrId => {
  markIdentityStandinCompleteSeen(userOrId);
  markIdentityStandinCompleteSeenOnServer().catch(() => {});
};

/**
 * TEST 18+ stand-in form. After success, shows Go to Dashboard + Find a listener.
 *
 * @param {Object} props
 * @param {Object} [props.currentUser]
 * @param {boolean} [props.currentUserHasListings]
 * @param {Object} [props.currentUserOwnListing]
 * @param {Function} [props.onVerified]
 * @param {boolean} [props.allowCompleteCard] After-verify ready card. Off on profile pages.
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const IdentitySandboxPanel = props => {
  const {
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    onVerified,
    allowCompleteCard = true,
    className,
  } = props;
  const intl = useIntl();
  const identity = identityFromUser(currentUser);
  const userId = currentUser?.id?.uuid;
  const [confirmedAge18, setConfirmedAge18] = useState(false);
  const [mockIdProvided, setMockIdProvided] = useState(false);
  const [selfieConfirmed, setSelfieConfirmed] = useState(false);
  const [mockLast4, setMockLast4] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [hideComplete, setHideComplete] = useState(false);
  const [justCompletedStandin, setJustCompletedStandin] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [sandboxAllowed, setSandboxAllowed] = useState(
    process.env.REACT_APP_ALLOW_DEMO_SESSIONS === 'true'
  );
  const [identityStatus, setIdentityStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchIdentityStatus()
      .then(response => {
        if (cancelled) {
          return;
        }
        const data = response?.data || response || {};
        setStripeConfigured(data.stripeConfigured === true);
        setSandboxAllowed(data.sandbox === true);
        setIdentityStatus(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userId, identity.isVerified]);

  const showComplete =
    allowCompleteCard &&
    identity.isVerified &&
    !hideComplete &&
    (justCompletedStandin || shouldShowIdentityStandinComplete(currentUser || userId));

  useEffect(() => {
    if (!userId || !identity.isVerified) {
      return;
    }
    if (!allowCompleteCard || showComplete) {
      persistStandinCompleteSeen(currentUser || userId);
    }
  }, [allowCompleteCard, currentUser, identity.isVerified, showComplete, userId]);

  const dismissComplete = () => {
    persistStandinCompleteSeen(currentUser || userId);
    setHideComplete(true);
  };

  if (identity.isVerified) {
    if (!showComplete) {
      return null;
    }
    return (
      <section
        id="identity"
        className={classNames(css.root, css.complete, className)}
        data-identity-standin-complete="true"
      >
        <h2 className={css.title}>
          <FormattedMessage id="Identity.verifiedTitle" />
        </h2>
        <p className={css.lede}>
          <FormattedMessage id="Identity.verifiedBody" />
        </p>
        <IdentityNextSteps
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserOwnListing={currentUserOwnListing}
          onLeave={dismissComplete}
        />
      </section>
    );
  }

  const last4Ok = /^\d{4}$/.test(mockLast4.trim());
  const canSubmit = confirmedAge18 && mockIdProvided && selfieConfirmed && last4Ok && !inProgress;

  const onSubmit = event => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    setInProgress(true);
    setError(null);
    sandboxVerifyIdentity({
      confirmedAge18: true,
      mockIdProvided: true,
      selfieConfirmed: true,
    })
      .then(() => {
        setInProgress(false);
        setMockLast4('');
        setJustCompletedStandin(true);
        persistStandinCompleteSeen(currentUser || userId);
        if (typeof onVerified === 'function') {
          onVerified();
        }
      })
      .catch(e => {
        setInProgress(false);
        setError(e?.error || e?.message || intl.formatMessage({ id: 'Identity.verifyFailed' }));
      });
  };

  const status = identityStatus?.status || identityStatus?.outcome || null;
  const unfinished =
    identityStatus?.retryable === true ||
    status === 'requires_input' ||
    status === 'canceled' ||
    status === 'missing_session';
  const retryable = unfinished && status !== 'underage';
  // Talkers see a short "Why we check" screen before Stripe opens (Sam, Oct 10).
  const showWhyWeCheck =
    getAccountRole(currentUser) === ACCOUNT_ROLE_TALK &&
    !retryable &&
    status !== 'requires_input' &&
    status !== 'manual_review_pending' &&
    status !== 'underage';

  return (
    <section id="identity" className={classNames(css.root, className)}>
      {stripeConfigured ? (
        <div className={css.stripeBlock}>
          {showWhyWeCheck ? (
            <div data-identity-why="true">
              <p className={css.kicker}>
                <FormattedMessage id="Identity.whyKicker" />
              </p>
              <h2 className={css.title}>
                <FormattedMessage id="Identity.whyTitle" />
              </h2>
              <p className={css.whyBody}>
                <FormattedMessage id="Identity.whyBody" />
              </p>
              <p className={css.lede}>
                <FormattedMessage id="Identity.whyUpload" />
              </p>
            </div>
          ) : (
            <>
              <p className={css.kicker}>
                <FormattedMessage id="Identity.stripeKicker" />
              </p>
              <h2 className={css.title}>
                <FormattedMessage id="Identity.stripeTitle" />
              </h2>
              <p className={css.lede}>
                <FormattedMessage id="Identity.stripeLede" />
              </p>
            </>
          )}
          {status === 'underage' ? (
            <p className={css.error} role="alert">
              <FormattedMessage id="Identity.underage" />
            </p>
          ) : null}
          {status === 'requires_input' || status === 'canceled' ? (
            <>
              <IdentityRetryNotice
                reason={status === 'canceled' ? 'abandoned' : identityStatus?.reason}
                headingLevel="h3"
              />
              {/* Oct 17 2026: photo upload only after a real Stripe failure (the server
                  refuses it otherwise), tucked under Try again so Stripe stays first. */}
              {status === 'requires_input' ? (
                <IdentityManualUpload
                  rejectReason={identityStatus?.manualRejectReason || null}
                  onUploaded={() =>
                    setIdentityStatus({
                      ...(identityStatus || {}),
                      status: 'manual_review_pending',
                      outcome: 'manual_review_pending',
                      retryable: false,
                      justUploaded: true,
                    })
                  }
                />
              ) : null}
            </>
          ) : null}
          {status === 'manual_review_pending' ? (
            <ManualPendingNotice
              className={css.retry}
              titleClassName={css.title}
              autoFocus={identityStatus?.justUploaded === true}
            />
          ) : null}
          {retryable && status !== 'requires_input' ? (
            <p className={css.retry} role="status" data-identity-status={status || 'unfinished'}>
              <FormattedMessage id="Identity.notFinishedContinue" />
            </p>
          ) : null}
          {status !== 'requires_input' && status !== 'manual_review_pending' ? (
            <a className={css.primaryCta} href={startStripeIdentityPath()}>
              <FormattedMessage
                id={retryable ? 'Identity.continueIdCheck' : 'Identity.startStripe'}
              />
            </a>
          ) : null}
          <p className={css.privacy}>
            <FormattedMessage id="Identity.stripePrivacy" />
          </p>
        </div>
      ) : null}
      {!stripeConfigured && !sandboxAllowed ? (
        <p className={css.lede}>
          <FormattedMessage id="Identity.unavailable" />
        </p>
      ) : null}
      {sandboxAllowed ? (
        <>
          <p className={css.kicker}>
            <FormattedMessage id="Identity.sandboxKicker" />
          </p>
          <h2 className={css.title}>
            <FormattedMessage id="Identity.title" />
          </h2>
          <p className={css.lede}>
            <FormattedMessage id="Identity.lede" />
          </p>
          <form className={css.form} onSubmit={onSubmit}>
            <label className={css.check}>
              <input
                type="checkbox"
                checked={confirmedAge18}
                onChange={event => setConfirmedAge18(event.target.checked)}
              />
              <span>
                <FormattedMessage id="Identity.confirmAge18" />
              </span>
            </label>
            <label className={css.check}>
              <input
                type="checkbox"
                checked={mockIdProvided}
                onChange={event => setMockIdProvided(event.target.checked)}
              />
              <span>
                <FormattedMessage id="Identity.confirmId" />
              </span>
            </label>
            <label className={css.field}>
              <span>
                <FormattedMessage id="Identity.mockLast4Label" />
              </span>
              <input
                className={css.input}
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                value={mockLast4}
                onChange={event => setMockLast4(event.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            </label>
            <label className={css.check}>
              <input
                type="checkbox"
                checked={selfieConfirmed}
                onChange={event => setSelfieConfirmed(event.target.checked)}
              />
              <span>
                <FormattedMessage id="Identity.confirmSelfie" />
              </span>
            </label>
            {error ? (
              <p className={css.error} role="alert">
                {error}
              </p>
            ) : null}
            <PrimaryButton type="submit" inProgress={inProgress} disabled={!canSubmit}>
              <FormattedMessage id="Identity.submitSandbox" />
            </PrimaryButton>
            <p className={css.privacy}>
              <FormattedMessage id="Identity.privacyNote" />
            </p>
          </form>
        </>
      ) : null}
    </section>
  );
};

export default IdentitySandboxPanel;
